import { IFormattedAddress, IPostcoderLine } from "./address.types";

export const sanitizeGBPostcode = (postcode: string): string => {
  const withoutWhitespace = postcode.replace(/\s/g, "").toUpperCase();
  if (withoutWhitespace.length > 3) return `${withoutWhitespace.substring(0, withoutWhitespace.length - 3)} ${withoutWhitespace.substring(withoutWhitespace.length - 3)}`;
  return withoutWhitespace;
};

const COUNTRY = "GB";

export const formatAddress = (address: IPostcoderLine, postcode: string): IFormattedAddress => {
  if (!address.buildingname && !address.premise) {
    return {
      formattedAddress: address.summaryline.split(", "),
      address1: "",
      address2: "",
      city: address.posttown,
      country: COUNTRY,
      postcode,
      phone: "",
    };
  }

  const line1 = address.street
    ? `${address.buildingname || address.premise} ${address.street}`
    : address.buildingname;

  const line2 = address.dependentlocality && address.posttown
    ? `${address.dependentlocality} ${address.posttown}`
    : address.dependentlocality || "";

  return {
    formattedAddress: address.summaryline.split(", "),
    address1: line1,
    address2: line2,
    city: address.posttown,
    country: COUNTRY,
    postcode,
    phone: "",
  };
};

export const sanitizeAddress = (str: string): string => (str || "").replace(/\W/g, "").toLowerCase();

export const combineAddressLines = (address: IFormattedAddress): string => {
  const lines = [
    address.address1,
    address.address2,
    address.city,
  ].filter(Boolean);
  return lines.join(" ");
};

/**
 * Calculates similarity between two strings using Levenshtein distance
 * @param s1 First string
 * @param s2 Second string
 * @returns Similarity ratio between 0 and 1
 */
export const calculateSimilarity = (s1: string, s2: string): number => {
  const editDistance = (str1: string, str2: string): number => {
    const a = str1.toLowerCase();
    const b = str2.toLowerCase();
    const matrix = Array(b.length + 1).fill(null).map(() => Array(a.length + 1).fill(null));

    for (let i = 0; i <= a.length; i += 1) { matrix[0][i] = i; }
    for (let j = 0; j <= b.length; j += 1) { matrix[j][0] = j; }

    for (let j = 1; j <= b.length; j += 1) {
      for (let i = 1; i <= a.length; i += 1) {
        const indicator = a[i - 1] === b[j - 1] ? 0 : 1;
        matrix[j][i] = Math.min(
          matrix[j][i - 1] + 1, // deletion
          matrix[j - 1][i] + 1, // insertion
          matrix[j - 1][i - 1] + indicator, // substitution
        );
      }
    }

    return matrix[b.length][a.length];
  };

  const longer = s1.length > s2.length ? s1 : s2;
  const shorter = s1.length > s2.length ? s2 : s1;

  if (longer.length === 0) { return 1.0; }

  return (longer.length - editDistance(longer, shorter)) / longer.length;
};

export const findBestMatch = (
  userAddress: string,
  validAddresses: IFormattedAddress[],
): { address: IFormattedAddress | null; similarity: number } => {
  let bestMatch: IFormattedAddress | null = null;
  let bestSimilarity = 0;

  for (const address of validAddresses) {
    const apiAddress = sanitizeAddress(combineAddressLines(address));
    const similarity = calculateSimilarity(userAddress, apiAddress);

    if (similarity > bestSimilarity) {
      bestMatch = address;
      bestSimilarity = similarity;
    }
  }

  return { address: bestMatch, similarity: bestSimilarity };
};
